import { Location, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { RequestAdditionalInfoModalComponent } from 'components/modal/request-additional-info-modal/request-additional-info-modal.component';
import { RequestAnotherTimeSpecificationComponent } from 'components/request-another-time-specification/request-another-time-specification.component';
import { ServiceFormActionsComponent } from 'components/service/service-form-actions/service-form-actions.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { TopicInfoComponent } from 'components/topic-info/topic-info.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { ReaderCardByClientId, ResearchTopicListItem } from 'types/client.interface';
import {
  BackofficeReadingRoomSubmissionCard,
  ReadingRoomSubmissionAttendanceStatusDto,
  ReadingRoomSubmissionCommunicationDto,
  ReadingRoomSubmissionCommunicationListItemDto,
  ReadingRoomSubmissionResearchTopicItem
} from 'types/service.interface';
import { AttendenceStatus, BackofficeStatus } from 'types/visit-room.interface';
import { formatDatetime } from 'util/date.utility';
import { SummaryDocumentRowComponent } from '../summary-document-row/summary-document-row.component';
import { FormsModule } from '@angular/forms';

export const TIME_FORMAT = 'HH:mm';

@Component({
  selector: 'app-service-summary',
  standalone: true,
  imports: [
    TranslateModule,
    NgClass,
    NgbCollapse,
    ServiceFormActionsComponent,
    TopicInfoComponent,
    FileUploadComponent,
    TooltipComponent,
    RequestAdditionalInfoModalComponent,
    RequestAnotherTimeSpecificationComponent,
    SummaryDocumentRowComponent,
    FormsModule
  ],
  templateUrl: './service-summary.component.html',
  styleUrls: [
    './service-summary.component.scss',
    '../visit-room/visit-room-topic-form/visit-room-topic-form.component.scss',
    '../../form/base-form.scss'
  ]
})
export class ServiceSummaryComponent implements OnInit {
  public readonly formatDate = formatDatetime;
  public readonly attendenceStatuses = AttendenceStatus;
  @Input() application!: BackofficeReadingRoomSubmissionCard;
  @Input() researchTopics!: ResearchTopicListItem[];
  @Input() clientId!: string;
  @Input() attendenceStatus!: ReadingRoomSubmissionAttendanceStatusDto;
  @Input() readersCard?: ReaderCardByClientId;
  @Input() isModal = false;
  @Output() applicationUpdated = new EventEmitter<BackofficeReadingRoomSubmissionCard>();
  @Output() messageSaved: EventEmitter<ReadingRoomSubmissionCommunicationListItemDto | ReadingRoomSubmissionCommunicationDto> =
    new EventEmitter();
  isRequestAdditionalInfoModalOpen: boolean = false;
  isRequestAnotherTimeSpecificationModalOpen: boolean = false;
  chosenTopics: string[] = [];
  researchTopicsCollapsed: boolean[] = [];
  isGeneralSectionCollapsed: boolean = false;
  isResearchSectionCollapsed: boolean = false;
  isDocumentsSectionCollapsed: boolean = false;
  isReadersInfoSectionCollapsed: boolean = false;
  isSubmitting: boolean = false;
  editTermsAgreement: boolean = false;
  agreedToTerms: boolean = false;
  isExcelDownloading: boolean = false;

  constructor(
    private apiService: ApiService,
    private toastService: ToastService,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.researchTopicsCollapsed = this.application.researchTopics?.map(() => true) || [];
  }

  goToStep(step: string): void {
    this.router.navigate([], {
      queryParams: { solis: step },
      queryParamsHandling: 'merge'
    });
  }

  formatTime(time: string | null | undefined): string {
    return time ? time.substring(0, TIME_FORMAT.length) : '-';
  }

  updateTopics(topic: ResearchTopicListItem): void {
    if (!this.chosenTopics.includes(topic.id!.toString())) {
      this.chosenTopics.push(topic.id!.toString());
    } else {
      this.chosenTopics = this.chosenTopics.filter((id) => id !== topic.id);
    }
  }

  submit(): void {
    this.isSubmitting = true;
    this.apiService.submitVisitRoomApplication(this.application.id!).subscribe(
      (data: BackofficeReadingRoomSubmissionCard) => {
        this.toastService.showSuccess('applicationSubmittedTitle', '', {
          shouldTranslate: true,
          delay: 0
        });
        window.scrollTo(0, 0);
        this.applicationUpdated.emit(data);
        this.application = data;
        this.isSubmitting = false;
        this.router.navigate(['pieteikums/' + this.application.id]);
      },
      (error: any) => {
        console.error(error);
        this.toastService.showFormValidationError();
        this.isSubmitting = false;
      }
    );
  }

  editReaderInfo(): void {
    this.router.navigate(['klients/' + this.application.client?.id], {
      queryParams: { activeItem: 'mans-profils' }
    });
  }

  back(): void {
    if (this.application.initializationStatus === 'InitializationDone') {
      this.location.back();
    } else {
      this.router.navigate([], {
        queryParams: { solis: 3 },
        queryParamsHandling: 'merge'
      });
    }
  }

  getResearchTopic(id: string): ReadingRoomSubmissionResearchTopicItem {
    return this.application.researchTopics?.find(
      (topic: ReadingRoomSubmissionResearchTopicItem) => topic.id === id
    ) as ReadingRoomSubmissionResearchTopicItem;
  }

  topicChanged() {
    this.applicationUpdated.emit(this.application);
  }

  requestAnotherTimeSuggestion() {
    this.isRequestAdditionalInfoModalOpen = true;
  }

  requestAnotherTimeSpecification() {
    this.isRequestAnotherTimeSpecificationModalOpen = true;
  }

  onMessageSaved(message: ReadingRoomSubmissionCommunicationListItemDto | ReadingRoomSubmissionCommunicationDto): void {
    this.messageSaved.emit(message);
    this.applicationUpdated.emit(this.application);
  }

  get shouldRenderChangeTimeButtons() {
    return (
      this.application?.backofficeStatus === BackofficeStatus.InProcess ||
      this.application?.backofficeStatus === BackofficeStatus.RequestedAdditionalInformation ||
      this.application?.backofficeStatus == BackofficeStatus.SubmittedAdditionalInformation ||
      this.application?.backofficeStatus == BackofficeStatus.Approved
    );
  }

  applicationScheduleUpdated(data: BackofficeReadingRoomSubmissionCard) {
    this.applicationUpdated.emit(data);
    this.isRequestAnotherTimeSpecificationModalOpen = false;
  }

  updateTermsAgreement() {
    this.apiService.visitRoomAcceptTerms(this.application.id).subscribe((data) => {
      this.applicationUpdated.emit(data);
      this.editTermsAgreement = false;
    });
  }

  downloadExcel() {
    this.isExcelDownloading = true;
    this.apiService.getReadingRoomApplicationDocumentExcel(this.application.id).subscribe((data: any) => {
      this.isExcelDownloading = false;
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pieteikuma_${this.application.code}_pasutitie_dokumenti.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }

  shouldShowApproveDocumentButton(): boolean {
    return [
      BackofficeStatus.InProcess,
      BackofficeStatus.RequestedAdditionalInformation,
      BackofficeStatus.SubmittedAdditionalInformation
    ].includes(this.application.backofficeStatus as BackofficeStatus);
  }
}
